import { Injectable } from '@angular/core';

import {
  ConnectedPhasesTypeEnum,
  FeedInManagementInfoEnum,
  GridRegistrationStatus,
  GridRegistrationStatusEnum,
  GridRegistrationTypeEnum,
  PhasesTypeEnum,
  PowerGenerationSourceEnum
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import {
  ActionTypeEnum,
  CommonStatusEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { UserDto } from '@bo-client/shared/models/userDto';

import { uri } from '@bo-client/shared/constants/uri';
import {
  ActionWithMoreInfoModel,
  CustomFormTypes,
  ExecutionActionsTypes,
  SecondActionTypes
} from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { AuthenticationService } from '@bo-client/core';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { CommissioningManagementService } from '@bo-client/activation-management/commissioning-management.service';
import { combineLatestWith, map, Observable, of } from 'rxjs';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { UserRole } from '@bo-client/shared/models/user-roles.enum';

@Injectable({
  providedIn: 'root'
})
export class GridRegistrationRequestService {
  actions: ActionWithMoreInfoModel[] = [
    {
      action: 'save',
      actionName: 'Zwischenspeichern',
      actionUrl: null,
      successMessage: null,
      failureMessage: null,
      subject: null,
      validateForm: false,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          entity.state === GridRegistrationStatusEnum.Incomplete ||
          entity.state === GridRegistrationStatusEnum.FeedbackNeeded ||
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.save,
      actionType: 'secondary'
    },
    {
      action: 'approve',
      actionName: 'Freigeben',
      actionUrl: uri.cmGridRegistrationApproved,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      successMessage: 'Netzanmeldung erfolgreich freigegeben',
      failureMessage: 'Fehler bei der Freigabe der Netzanmeldung',
      recipients: 'Kunde / Installateur',
      subject: 'Ihre Netzanmeldung',
      validateForm: false,
      customForm: CustomFormTypes.GrCustomApprove,
      actionType: 'primary',
      tooltip:
        'Versetzt den Vorgang in den Status <i>Freigegeben</i> und versendet eine E-Mail mit der Netzzusage an den Kunden und den verantwortlichen Installateur.',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          !(
            entity.state === GridRegistrationStatusEnum.Approved ||
            entity.state === GridRegistrationStatusEnum.PendingAssignTechnician ||
            entity.state === GridRegistrationStatusEnum.WaitingForCustomerFeedInOrder ||
            entity.state === GridRegistrationStatusEnum.ConfiguringFeedInManagement ||
            entity.state === GridRegistrationStatusEnum.PlaceInServiceConfirmationRequest ||
            entity.state === GridRegistrationStatusEnum.PlacedInServiceConfirmation
          ) &&
          entity.gridRegistrationType !== GridRegistrationTypeEnum.PlugInProducer &&
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    },
    {
      action: 'sendToReview',
      actionName: 'zur Prüfung',
      actionUrl: uri.cmGridRegistrationSendToReview,
      successMessage: 'Netzanmeldung in Prüfung geschickt',
      failureMessage: 'Fehler beim Übergeben der Netzanmeldung in die Prüfung',
      recipients: 'Kunde / Installateur',
      subject: 'Ihre Netzanmeldung ist jetzt in Prüfung',
      validateForm: false,
      actionType: 'primary',
      tooltip:
        'Versetzt den Vorgang in den Status <i>in Prüfung</i> und benachrichtigt den Kunden und den Installateur',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          !(
            entity.state === GridRegistrationStatusEnum.Approved ||
            entity.state === GridRegistrationStatusEnum.PendingAssignTechnician ||
            entity.state === GridRegistrationStatusEnum.WaitingForCustomerFeedInOrder ||
            entity.state === GridRegistrationStatusEnum.ConfiguringFeedInManagement ||
            entity.state === GridRegistrationStatusEnum.UnderReview ||
            entity.state === GridRegistrationStatusEnum.PlaceInServiceConfirmationRequest ||
            entity.state === GridRegistrationStatusEnum.PlacedInServiceConfirmation
          ) &&
          entity.gridRegistrationType !== GridRegistrationTypeEnum.PlugInProducer &&
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'completeReview',
      actionName: 'techn. Prüfung abschließen',
      actionUrl: uri.cmGridRegistrationCompleteReview,
      successMessage: 'Daten übermittelt',
      failureMessage: 'Fehler beim Abschluss der Prüfung',
      recipients: '-',
      subject: '-',
      validateForm: false,
      actionType: 'primary',
      tooltip: 'Versetzt den Vorgang in den Status <i>technische Prüfung abgeschlossen</i>',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          entity.state === GridRegistrationStatusEnum.UnderReview &&
          entity.gridRegistrationType !== GridRegistrationTypeEnum.PlugInProducer &&
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'pluginApprove',
      actionName: 'Freigeben',
      actionUrl: uri.cmGridRegistrationApproved,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      successMessage: 'Steckerfertige Erzeugungsanlage erfolgreich freigegeben',
      failureMessage: 'Fehler bei der Freigabe der Steckerfertigen Erzeugungsanlage',
      recipients: 'Technician',
      subject: 'Ihre steckerfertige Erzeugungsanlage',
      validateForm: false,
      customForm: CustomFormTypes.GrPluginCustomApprove,
      actionType: 'primary',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          entity.gridRegistrationType === GridRegistrationTypeEnum.PlugInProducer &&
          (entity.state === GridRegistrationStatusEnum.Requested ||
            (entity.state === GridRegistrationStatusEnum.NeedMeterChange &&
              this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])))
        );
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal,
      tooltip:
        'Versetzt den Vorgang in den Status <i>Freigegeben</i> und versendet eine E-Mail mit der Netzzusage an den Kunden und den verantwortlichen Installateur.'
    },

    {
      action: 'requestFeedback',
      actionName: 'Rückfragen',
      actionUrl: uri.cmGridRegistrationRequestFeedback,
      successMessage: 'Rückfrage versandt',
      failureMessage: 'Fehler beim Versenden der Rückfrage',
      subject: 'Rückfrage zu Ihrer Netzanmeldung',
      validateForm: false,
      actionType: 'primary',
      tooltip:
        'Versetzt den Vorgang in den Status <i>Rückfrage</i> und versendet eine E-Mail an den Beantragenden mit dem Rückfrage-Text. - Kunden ohne Login bekommen einen Link um die Rückfrage direkt zu beantworten.',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          entity.state === GridRegistrationStatusEnum.Requested &&
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'requestApproval',
      actionName: 'Genehmigung anfragen',
      actionUrl: uri.cmGridRegistrationRequestApproval,
      successMessage: 'Anfrage erfolgreich versandt',
      failureMessage: 'Fehler beim Versenden der Anfrage',
      subject: 'Ihre Anfrage zur Netzanmeldung',
      validateForm: true,
      actionType: 'primary',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          entity.state === GridRegistrationStatusEnum.Incomplete ||
          entity.state === GridRegistrationStatusEnum.FeedbackNeeded
        );
      },
      execute: ExecutionActionsTypes.saveAndActionPlusMail
    },
    {
      action: 'confirmAction',
      actionName: 'Antrag korrigieren',
      actionUrl: uri.cmGridRegistrationCorrectBeforeApproval,
      successMessage: 'Anfrage erfolgreich nach unvollständig gesetzt',
      failureMessage: 'Fehler beim Versenden der Anfrage',
      subject: 'Ihre Anfrage zur Netzanmeldung',
      validateForm: false,
      actionType: 'primary',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          state === GridRegistrationStatusEnum.Requested &&
          this.hasOneOfRoles(user, ['ROLE_INSTALLATEUR', 'ROLE_ADMIN_INSTALLATEUR'])
        );
      },
      execute: ExecutionActionsTypes.confirmAction
    },
    {
      action: 'declined',
      actionName: 'Ablehnen',
      actionUrl: uri.cmGridRegistrationDeclined,
      successMessage: 'Netzanmeldung abgelehnt',
      failureMessage: 'Fehler beim Ablehnen der Netzanmeldung',
      subject: 'Netzanmeldung abgelehnt',
      validateForm: false,
      actionType: 'primary',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          !(entity.state === GridRegistrationStatusEnum.Declined) &&
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'requestCustomerPlaceInServices',
      actionName: 'Inbetriebsetzungsprotokoll erstellen',
      actionUrl: uri.cmCRequestConfirmationCustomerPlaceService,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      successMessage: 'Anfrage per E-Mail erfolgreich versandt',
      failureMessage: 'Fehler beim Versenden der Anfrage per E-Mail',
      subject: 'Bitte bestätigen Sie die Daten aus dem Inbetriebsetzungsprotokoll.',
      customForm: CustomFormTypes.PlaceInServicesRequestedForm,
      validateForm: true,
      infoInButton: 'Diese Aktion ist nicht verfügbar, weil Sie keinen gültigen Strom Ausweis haben',
      actionType: 'primary',
      tooltip:
        'Erstellen Sie als eingetragener Elektroinstallateur das Inbetriebsetzungsprotokoll online im Portal. Der Kunde erhält eine E-Mail mit der Bitte, die Daten zu bestätigen, sollten Sie keine Vollmacht haben.',
      disabled: (user: UserDto): boolean => {
        return this.isInstallerWithoutValidPowerPass(user) && !this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE']);
      },
      visibleCondition: (entity: GridRegistrationModel, state: string, user: UserDto): boolean => {
        return (
          entity.state === GridRegistrationStatusEnum.Approved &&
          entity.gridRegistrationType === GridRegistrationTypeEnum.Feeder &&
          (!this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE']) ||
            this.hasOneOfRoles(user, ['ROLE_KEYUSER_GRIDREGISTRATION']))
        );
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    },
    {
      action: 'commissioning',
      actionName: 'Inbetriebnahme Antrag erstellen',
      actionUrl: uri.cmGridRegistrationDeclined,
      successMessage: 'Inbetriebnahme Antrag erstellt',
      failureMessage: 'Fehler beim Erstellen des Antrags',
      subject: 'Inbetriebnahme erstellt',
      validateForm: false,
      infoInButton: 'Diese Aktion ist nicht verfügbar, weil Sie keinen gültigen Strom Ausweis haben',
      actionType: 'primary',
      tooltip: 'Erstellt einen neuen Inbetriebnahme Antrag und übernimmt die bereits ausgefüllten Daten.',
      disabled: (user: UserDto): boolean => {
        return (
          !this.commissioningManagementService.hasValidPassByArea(user, {
            id: 2,
            name: 'POWER',
            description: 'Strom'
          }) && !this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])
        );
      },
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          (entity.gridRegistrationType !== GridRegistrationTypeEnum.Feeder &&
            entity.gridRegistrationType !== GridRegistrationTypeEnum.PlugInProducer &&
            entity.state === GridRegistrationStatusEnum.Approved &&
            !this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])) ||
          (entity.gridRegistrationType === GridRegistrationTypeEnum.Feeder &&
            entity.state === GridRegistrationStatusEnum.PlacedInServiceConfirmation &&
            !this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])) ||
          (entity.gridRegistrationType === GridRegistrationTypeEnum.PlugInProducer &&
            this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE']))
        );
      },
      execute: ExecutionActionsTypes.createCommissioningFromGriReg
    },
    {
      action: 'shareGridRegistration',
      actionName: 'Netzanmeldung übertragen',
      actionUrl: uri.cmGRTransferToTechnicianRequest,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      successMessage: 'Link zur Übergabe der Anfrage versandt',
      failureMessage: 'Fehler bei Übergeben der Netzanmeldung.',
      recipients: 'Installateur',
      subject: 'Netzanmeldung übergeben',
      validateForm: false,
      customForm: CustomFormTypes.GrTransferToTechnician,
      actionType: 'secondary',
      tooltip:
        'Überträgt die Netzanmeldung an einen anderen Benutzer des Portals. In der Regel ein Elektro-Installateur. Der Installateur erhält eine E-Mail mit einem Link, um die Netzanmeldung zu übernehmen.',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          entity.state === GridRegistrationStatusEnum.Approved ||
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    },
    {
      action: 'PlaceInServiceConfirmed',
      actionName: 'Daten bestätigen.',
      actionUrl: uri.cmCRPlaceServiceConfirmed,
      successMessage: 'Danke, wir haben Ihre Bestätigung erhalten.',
      failureMessage: 'Beim Senden der Bestätigung ist ein Fehler aufgetreten.',
      subject: '',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return !this.authenticationService.isLoggedIn;
      },
      actionType: 'primary',
      execute: ExecutionActionsTypes.executeAction
    },
    {
      action: 'PopulateMaStNummern',
      actionName: 'MaStR-Nummern melden',
      customForm: CustomFormTypes.MaStRNumberForm,
      actionUrl: uri.cmCloseCommissioningRequest,
      secondAction: SecondActionTypes.saveModel,
      successMessage: 'MaStR-Nummern erfolgreich gespeichert',
      failureMessage: 'Fehler beim MaStR-Nummern Speicherung',
      subject: '',
      validateForm: false,
      actionType: 'secondary',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.authenticationService.isLoggedIn &&
          !entity.maStRNumber &&
          entity.gridRegistrationType === GridRegistrationTypeEnum.Feeder &&
          (entity.state === GridRegistrationStatusEnum.Approved ||
            entity.state === GridRegistrationStatusEnum.PlaceInServiceConfirmationRequest ||
            entity.state === GridRegistrationStatusEnum.PlacedInServiceConfirmation)
        );
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    },
    {
      action: 'notifyMaloMelo',
      actionName: 'MaLo/Melo Mitteilen',
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      customForm: CustomFormTypes.MaLoMeloForm,
      actionUrl: uri.cmSendMaloMeloMail,
      successMessage: 'Nachricht erfolgreich verschickt',
      failureMessage: 'Fehler beim Verschicken der Nachricht',
      subject: 'Ihre MaLo / MeLo',
      validateForm: false,
      actionType: 'secondary',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.dataShareService.tenantConfig.gridRegistration?.showMaloMeLoAction &&
          this.authenticationService.isLoggedIn &&
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE']) &&
          this.dataShareService.tenantConfig.gridRegistration?.showMaloMeLoAction &&
          entity.gridRegistrationType === GridRegistrationTypeEnum.Feeder &&
          (entity.state === GridRegistrationStatusEnum.Approved ||
            entity.state === GridRegistrationStatusEnum.ConfiguringFeedInManagement ||
            entity.state === GridRegistrationStatusEnum.WaitingForCustomerFeedInOrder ||
            entity.state === GridRegistrationStatusEnum.PlaceInServiceConfirmationRequest ||
            entity.state === GridRegistrationStatusEnum.PlacedInServiceConfirmation)
        );
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    },
    {
      action: 'notifyMaloMelo',
      actionName: 'Tr/Sr Mitteilen',
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      customForm: CustomFormTypes.SrTrForm,
      actionUrl: uri.cmSendTrSrMail,
      successMessage: 'Nachricht erfolgreich verschickt',
      failureMessage: 'Fehler beim Verschicken der Nachricht',
      subject: 'Ihre Tr/Sr Nummern',
      validateForm: false,
      actionType: 'secondary',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.dataShareService.tenantConfig.gridRegistration.showSrTrAction &&
          this.authenticationService.isLoggedIn &&
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE']) &&
          (entity.state === GridRegistrationStatusEnum.Approved ||
            entity.state === GridRegistrationStatusEnum.ConfiguringFeedInManagement ||
            entity.state === GridRegistrationStatusEnum.WaitingForCustomerFeedInOrder ||
            entity.state === GridRegistrationStatusEnum.PlaceInServiceConfirmationRequest ||
            entity.state === GridRegistrationStatusEnum.PlacedInServiceConfirmation)
        );
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    },
    {
      action: 'AcceptFeedingContract',
      actionName: 'Angaben bestätigen',
      actionUrl: uri.cmGRConfirmFeedinOrder,
      successMessage: 'Bestätigung erfolgreich gesendet',
      failureMessage: 'Fehler beim Senden der Bestätigung',
      subject: '',
      validateForm: true,
      actionType: 'primary',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return !this.authenticationService.isLoggedIn;
      },
      execute: ExecutionActionsTypes.executeAction
    },
    {
      action: 'confirmFeedinConfirguration',
      actionName: 'Bereitstellung Einspeisemgmt.',
      actionUrl: uri.cmGRConfirmFeedinConfigurationr,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      successMessage: 'Einspeisemanagement erfolgreich bereitgestellt.',
      failureMessage: 'Fehler bei der Bereitstellung des Einspeisemanagements',
      recipients: 'Kunde / Installateur',
      subject: 'Ihr Einspeisermanagement steht bereit',
      validateForm: false,
      customForm: CustomFormTypes.GrConfirmFeedingConfiguration,
      actionType: 'primary',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          entity.state === GridRegistrationStatusEnum.ConfiguringFeedInManagement &&
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    },
    {
      action: 'Completed',
      actionName: 'Abschließen',
      actionUrl: uri.cmGRCompleted,
      successMessage: 'Einspeisevertrag erfolgreich abgeschlossen',
      failureMessage: 'Fehler beim abschließen der Netzanmeldung',
      subject: 'Netzanmeldung schließen',
      actionType: 'primary',
      validateForm: false,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          entity.state === GridRegistrationStatusEnum.Approved &&
          entity.gridRegistrationType === GridRegistrationTypeEnum.PlugInProducer &&
          this.hasOneOfRoles(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.confirmAction
    },
    {
      action: 'checkIfErrors',
      actionName: 'Fehler prüfen',
      actionUrl: null,
      successMessage: '',
      failureMessage: '',
      subject: '',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return false;
      },
      execute: ExecutionActionsTypes.checkIfErrors
    },
    {
      action: 'printPdf2',
      actionName: 'PDF erzeugen',
      actionUrl: '',
      successMessage: 'Dokument erfolgreich erzeugt',
      failureMessage: 'Fehler beim Erzeugen des Dokumentes',
      subject: '',
      validateForm: false,
      actionType: 'secondary',
      visibleCondition: (_, state: CommonStatusEnum | GridRegistrationStatus, user): boolean => {
        const allowedStates: (CommonStatusEnum | GridRegistrationStatus)[] = [
          GridRegistrationStatusEnum.Approved,
          GridRegistrationStatusEnum.Completed,
          GridRegistrationStatusEnum.Requested,
          GridRegistrationStatusEnum.PlacedInServiceConfirmation
        ];
        return (
          this.hasOneOfRoles(user, [
            'ROLE_EDITOR_GRIDREGISTRATIONS',
            'ROLE_INSTALLATEUR',
            'ROLE_ADMIN_INSTALLATEUR'
          ]) && allowedStates.includes(state)
        );
      },
      execute: ExecutionActionsTypes.createDocumentV2
    },
    {
      action: 'createExternalGridRegistration',
      actionName: 'Daten bestätigen.',
      actionUrl: uri.cmGridRegistrationRequestExternal,
      successMessage: 'Danke, wir haben Ihre Anfrage erhalten.',
      failureMessage: 'Beim Senden der Anfrage ist ein Fehler aufgetreten.',
      subject: '',
      validateForm: true,
      actionType: 'primary',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return !this.authenticationService.isLoggedIn;
      },
      execute: ExecutionActionsTypes.createExternalGridRegistration
    },
    {
      action: 'acceptAssignment',
      actionName: 'Zuweisung bestätigen',
      actionUrl: uri.cmGrAssignToTechnicianConfirmation,
      successMessage: 'Netzanmeldung erfolgreich zugewiesen',
      failureMessage: 'Fehler beim Senden der Anfrage',
      subject: '',
      validateForm: true,
      actionType: 'primary',
      tooltip: 'Überträgt die Netzanmeldung an den aktuell angemeldeten Benutzer.',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return !this.authenticationService.isLoggedIn;
      },
      execute: ExecutionActionsTypes.executeAction
    },
    {
      action: 'shareGridRegistration',
      actionName: 'Status ändern',
      actionUrl: uri.cmGRChangeStatus,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      successMessage: 'Der Status wurde geändert',
      failureMessage: 'Fehler bei Statusänderung der Netzanmeldung.',
      recipients: 'Installateur',
      subject: 'Status ändern',
      validateForm: false,
      customForm: CustomFormTypes.GrChangeStatus,
      actionType: 'secondary',
      tooltip: 'Keyuser Funktion um die Netzanmeldung in einen gewünschten Status zu versetzen.',
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return this.hasOneOfRoles(user, ['ROLE_KEYUSER_GRIDREGISTRATION']);
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    }
  ];
  phasesType: any[] = [
    {
      value: PhasesTypeEnum.L1,
      displayName: 'L1'
    },
    {
      value: PhasesTypeEnum.L2,
      displayName: 'L2'
    },
    {
      value: PhasesTypeEnum.L3,
      displayName: 'L3'
    }
  ];
  numberOfPhasesType: any[] = [
    {
      value: PhasesTypeEnum.L1,
      displayName: '1'
    },
    {
      value: PhasesTypeEnum.L2,
      displayName: '2'
    },
    {
      value: PhasesTypeEnum.L3,
      displayName: '3'
    }
  ];
  connectedPhasesType: any[] = [
    {
      value: ConnectedPhasesTypeEnum.L1,
      displayName: 'L1'
    },
    {
      value: ConnectedPhasesTypeEnum.L2,
      displayName: 'L2'
    },
    {
      value: ConnectedPhasesTypeEnum.L3,
      displayName: 'L3'
    },
    {
      value: ConnectedPhasesTypeEnum.L1L2,
      displayName: 'L1, L2'
    },
    {
      value: ConnectedPhasesTypeEnum.L1L3,
      displayName: 'L1, L3'
    },
    {
      value: ConnectedPhasesTypeEnum.L2L3,
      displayName: 'L2, L3'
    },
    {
      value: ConnectedPhasesTypeEnum.L1L2L3,
      displayName: 'L1, L2, L3'
    }
  ];
  PowerGenerationSourceType = [
    {
      value: PowerGenerationSourceEnum.PhotoVoltaic,
      displayName: 'Photovoltaik'
    },
    {
      value: PowerGenerationSourceEnum.Wind,
      displayName: 'Wind'
    },
    {
      value: PowerGenerationSourceEnum.Water,
      displayName: 'Wasser'
    },
    {
      value: PowerGenerationSourceEnum.BioGas,
      displayName: 'BHKW - Biogas'
    },
    {
      value: PowerGenerationSourceEnum.NaturalGas,
      displayName: 'BHKW - Erdgas'
    }
  ];

  gridRegistrationFeederActionType: any[] = [
    {
      value: ActionTypeEnum.commissioning,
      displayName: 'Neueinrichtung'
    },
    {
      value: ActionTypeEnum.extension,
      displayName: 'Erweiterung'
    }
  ];

  FeedInManagementInfo = [
    {
      value: FeedInManagementInfoEnum.noReduction,
      optionTitle: null,
      optionDescription: 'Für Ihre Leistung ist kein Einspeisermanagement oder Leistungsreduzierung nötig.',
      title: 'Anlagenleistung ≤ 25 kW (§ 9 Abs. 2 Nr. 2 a EEG)',
      titleDescription: '',
      minPower: 0,
      maxPower: 25
    },
    {
      value: FeedInManagementInfoEnum.controlWithClsFeedInReduction60,
      optionTitle: null,
      optionDescription: 'Zukünftig Steuerbox / Leistungsreduzierung auf 60%.',
      title: 'Anlagenleistung ≤ 25 kW (§ 9 Abs. 2 Nr. 2 a EEG)',
      titleDescription: '',
      minPower: 0,
      maxPower: 25,
      feedInCompensation: true
    },
    {
      value: FeedInManagementInfoEnum.electronicRippleControlReceiverFI60,
      optionTitle: null,
      optionDescription:
        'Rundsteuerempfänger inklusive Programmierung + Leistungsreduzierung 60% / Zukünftig neue Steuerungstechnik',
      title: 'Anlagenleistung > 25 kW und ≤ 100 kW ((§ 9 Abs. 2 Nr. 1 EEG)',
      titleDescription:
        'Ausstattung der Erzeugungsanlagen mit einer Anlagenleistung von mehr als 25 kW und höchstens 100 kW mit einer Einrichtung zur ferngesteuerten Reduzierung der Einspeiseleistung gemäß den Vorgaben.',
      minPower: 25,
      maxPower: 100,
      feedInCompensation: true
    },
    {
      value: FeedInManagementInfoEnum.electronicRippleControlReceiverWithProgramming,
      optionTitle: null,
      optionDescription: 'Rundsteuerempfänger inklusive Programmierung',
      title: 'Anlagenleistung > 25 kW und ≤ 100 kW ((§ 9 Abs. 2 Nr. 1 EEG)',
      titleDescription:
        'Ausstattung der Erzeugungsanlagen mit einer Anlagenleistung von mehr als 25 kW und höchstens 100 kW mit einer Einrichtung zur ferngesteuerten Reduzierung der Einspeiseleistung gemäß den Vorgaben.',
      minPower: 25,
      maxPower: 100
    },
    {
      value: FeedInManagementInfoEnum.telecontrolWithProgramming,
      optionTitle: null,
      optionDescription:
        'Fernwirktechnik zur Abschaltung/Übertragung der Ist-Einspeisewerte inklusive Programmierung',
      title: 'Anlagenleistung > 100 kW (§ 9 Abs. 1 EEG)',
      titleDescription:
        'Ausstattung der Erzeugungsanlagen mit einer Einrichtung zur ferngesteuerten Reduzierung der Einspeiseleistung und mit einer Einrichtung zur Abrufung der jeweiligen Ist-Einspeisung gemäß den Vorgaben.',
      minPower: 100,
      maxPower: 10000
    },
    {
      value: FeedInManagementInfoEnum.telecontrolWithProgramming2,
      optionTitle: null,
      optionDescription:
        'Fernwirktechnik zur Abschaltung/Übertragung der Ist-Einspeisewerte inklusive Programmierung',
      title: 'Anlagenleistung > 100 kW (§ 9 Abs. 1 EEG)',
      titleDescription:
        'Ausstattung der Erzeugungsanlagen mit einer Einrichtung zur ferngesteuerten Reduzierung der Einspeiseleistung und mit einer Einrichtung zur Abrufung der jeweiligen Ist-Einspeisung gemäß den Vorgaben.',
      minPower: 100,
      maxPower: 10000
    }
  ];

  gridRegistrationTypes$ = of([
    {
      value: 'NEW',
      title: 'Neuanschluss',
      displayName: 'Neuanschluss'
    },
    {
      value: 'CHANGE',
      title: 'Anlagen- und Anschlussveränderung',
      displayName: 'Anlagen- und Anschlussveränderung'
    },
    {
      value: 'DECOMMISSIONING',
      title: 'Stilllegung',
      displayName: 'Stilllegung'
    }
  ]);

  storageModeOfOperation$ = of([
    {
      value: 'NO_CONSUMPTION_NO_PRODUCTION',
      title: 'Kein Bezug und keine Einspeisung'
    },
    {
      value: 'NO_CONSUMPTION_PRODUCTION',
      title: 'Kein Bezug aber Einspeisung'
    },
    {
      value: 'CONSUMPTION_NO_PRODUCTION',
      title: 'Bezug und keine Einspeisung'
    },
    {
      value: 'CONSUMPTION_PRODUCTION',
      title: 'Bezug und Einspeisung'
    }
  ]);

  storageModeOfOperationFiltered$: Observable<{ value: string; title: string }[]>;

  constructor(
    private authenticationService: AuthenticationService,
    private commissioningManagementService: CommissioningManagementService,
    private dataShareService: DataShareService,
    private authorizationService: AuthorizationService
  ) {
    this.storageModeOfOperationFiltered$ = this.storageModeOfOperation$.pipe(
      combineLatestWith(this.dataShareService.tenantConfig$),
      map((x) => {
        console.log('filtering the shit out of it', x[1].gridRegistration.p14aStorageOperationOptions);
        if (x[1].gridRegistration.p14aStorageOperationOptions) {
          return x[0].filter((y) => x[1].gridRegistration.p14aStorageOperationOptions.includes(y.value));
        }
        return x[0];
      })
    );
  }

  hasOneOfRoles(user: UserDto, roles: UserRole[]) {
    return user.roles.find((userRole) => roles.includes(userRole.name)) !== undefined;
  }

  isInstallerWithoutValidPowerPass(user: UserDto) {
    return (
      this.hasOneOfRoles(user, ['ROLE_INSTALLATEUR', 'ROLE_ADMIN_INSTALLATEUR']) &&
      !this.commissioningManagementService.hasValidPassByArea(user, {
        id: 2,
        name: 'POWER',
        description: 'Strom'
      })
    );
  }
}
